import React from 'react';
import SpotSummary from 'domain/SpotSummary';
import { styled } from 'theme';
import { useTheme } from '@mui/material/styles';
import { alpha, Box } from '@mui/material';

const MarkerDivObj = styled(Box)<{
    size: number;
    color: string;
    fontColor: string;
    shadowColor: string;
}>(({ size, color, fontColor, shadowColor, theme }) => ({
    'display': 'grid',
    'gridTemplateRows': '1fr',
    'width': `${size}px`,
    'height': `${size}px`,
    'strokeWidth': '8px',
    'borderRadius': `${Math.floor(size / 2)}px`,
    'backgroundColor': alpha(color, 0.7),
    'fontSize': '20px',
    'boxShadow': theme.defaultShadow(shadowColor),
    'textShadow': theme.defaultShadow(shadowColor),
    'color': fontColor,
    '& p': {
        alignSelf: 'center',
        fontWeight: 'bold',
        marginBottom: 0,
        marginTop: 0,
        fontSize: '1.5em',
    },
}));

interface MarkerProps {
    spotSummary: SpotSummary;
    maxNumber: number;
    minNumber: number;
}

const getColor = (value: number, min: number, max: number, minColor: string, maxColor: string): string => {
    const hexToRgb = (hex: string) => {
        const bigint = parseInt(hex.slice(1), 16);
        const r = (bigint >> 16) & 255;
        const g = (bigint >> 8) & 255;
        const b = bigint & 255;
        return [r, g, b];
    };
    const rgbToHex = (r: number, g: number, b: number) => {
        const componentToHex = (c: number) => {
            const hex = c.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        };
        return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
    };

    // lerp is a helper function to linearly interpolate between two numbers a and b by a factor t.
    // When t is 0, the result is a, and when t is 1, the result is b.
    // For values of t between 0 and 1, the result will be a number between a and b.
    const lerp = (a: number, b: number, t: number) => a * (1 - t) + b * t;

    // clamp is a helper function to constrain a number num within the range [min, max].
    const clamp = (num: number, min: number, max: number) => Math.min(Math.max(num, min), max);

    const percent = (value - min) / (max - min);
    const t = clamp(!isNaN(percent) ? percent : 1, 0, 1);
    const [r1, g1, b1] = hexToRgb(minColor);
    const [r2, g2, b2] = hexToRgb(maxColor);

    const r = Math.round(lerp(r1, r2, t));
    const g = Math.round(lerp(g1, g2, t));
    const b = Math.round(lerp(b1, b2, t));

    return rgbToHex(r, g, b);
};

interface MarkerDivProps {
    mainNumber: number,
    maxNumber: number,
    minNumber: number,
}

const MarkerDiv = (props: MarkerDivProps) => {
    const theme = useTheme();
    const { mainNumber, maxNumber, minNumber } = props;
    const value = Math.min(Math.max(mainNumber, minNumber), maxNumber);
    let percentage = (value - minNumber) / (maxNumber - minNumber);
    percentage = !isNaN(percentage) ? percentage : 1;
    const size = 40 + percentage * 40;
    const color = getColor(mainNumber, minNumber, maxNumber, theme.palette.primary.main, theme.palette.secondary.main);
    const shadowColor = getColor(
        mainNumber,
        minNumber,
        maxNumber,
        theme.palette.primary.shadow,
        theme.palette.secondary.shadow
    );
    const fontColor = getColor(
        mainNumber,
        minNumber,
        maxNumber,
        theme.palette.primary.contrastText,
        theme.palette.secondary.contrastText
    );
    return (
        <MarkerDivObj size={size} color={color} fontColor={fontColor} shadowColor={shadowColor}>
            <p>{mainNumber}</p>
        </MarkerDivObj>
    );
};

const MarkerSummary = (props: MarkerProps) => {
    const { spotSummary } = props;
    return <MarkerDiv mainNumber={spotSummary.sum} maxNumber={props.maxNumber} minNumber={props.minNumber}/>;
};

export default MarkerSummary;
